/**
 * Enhanced Event System with Action Chaining
 * Supports admin-configurable actions and event chaining
 */

class EnhancedEventSystem {
    constructor() {
        this.actions = new Map();
        this.presetActions = new Map();
        this.initialized = false;
        this.debugMode = false;
    }

    /**
     * Initialize the event system
     */
    async init(debugMode = false) {
        if (this.initialized) {
            console.warn('EventSystem already initialized');
            return;
        }

        this.debugMode = debugMode;
        
        try {
            await this.loadActions();
            await this.loadPresetActions();
            this.registerEventListeners();
            this.registerActionChainListener();
            this.initialized = true;
            
            this.log(`EventSystem initialized with ${this.actions.size} actions and ${this.presetActions.size} presets`);
        } catch (error) {
            console.error('Failed to initialize EventSystem:', error);
        }
    }

    /**
     * Load all custom actions from /src/actions
     */
    async loadActions() {
        const actionModules = import.meta.glob('/frontend/src/actions/*.jsx', { eager: true });
        
        for (const [path, module] of Object.entries(actionModules)) {
            try {
                const actionName = this.extractActionName(path);
                const actionFunction = module[actionName] || module.default;
                
                if (typeof actionFunction === 'function') {
                    this.registerAction(actionName, actionFunction, 'custom');
                } else {
                    console.warn(`Action file ${path} does not export a function named ${actionName}`);
                }
            } catch (error) {
                console.error(`Failed to load action from ${path}:`, error);
            }
        }
    }

    /**
     * Load preset actions that admins can configure
     */
    async loadPresetActions() {
        const presets = {
            // Visibility controls
            showElement: (data) => this.toggleElementVisibility(data, true),
            hideElement: (data) => this.toggleElementVisibility(data, false),
            toggleElement: (data) => this.toggleElementVisibility(data, 'toggle'),
            
            // Navigation actions
            openCart: () => this.triggerCartAction('open'),
            closeCart: () => this.triggerCartAction('close'),
            toggleCart: () => this.triggerCartAction('toggle'),
            
            // Modal actions
            openModal: (data) => this.triggerModalAction(data, 'open'),
            closeModal: (data) => this.triggerModalAction(data, 'close'),
            toggleModal: (data) => this.triggerModalAction(data, 'toggle'),
            
            // Animation actions
            fadeIn: (data) => this.triggerAnimation(data, 'fadeIn'),
            fadeOut: (data) => this.triggerAnimation(data, 'fadeOut'),
            slideUp: (data) => this.triggerAnimation(data, 'slideUp'),
            slideDown: (data) => this.triggerAnimation(data, 'slideDown'),
            
            // Scroll actions
            scrollToElement: (data) => this.scrollToElement(data),
            scrollToTop: () => this.scrollToTop(),
            
            // Form actions
            submitForm: (data) => this.submitForm(data),
            resetForm: (data) => this.resetForm(data),
            
            // Analytics actions
            trackEvent: (data) => this.trackAnalyticsEvent(data),
            
            // Utility actions
            delay: (data) => this.delay(data.duration || 1000),
            redirect: (data) => this.redirect(data.url),
            refresh: () => window.location.reload(),
            
            // Custom event dispatch
            dispatchCustomEvent: (data) => this.dispatchCustomEvent(data)
        };

        for (const [name, action] of Object.entries(presets)) {
            this.presetActions.set(name, action);
            this.registerAction(name, action, 'preset');
        }
    }

    /**
     * Register an action with the event system
     */
    registerAction(actionName, actionFunction, type = 'custom') {
        const eventName = this.getEventName(actionName);
        
        this.actions.set(eventName, {
            name: actionName,
            function: actionFunction,
            eventName: eventName,
            type: type
        });

        this.log(`Registered ${type} action: ${actionName} -> event: ${eventName}`);
    }

    /**
     * Convert action name to event name
     */
    getEventName(actionName) {
        let eventAction = actionName;
        
        // Strip arc:action: prefix if already present (prevent double-prefixing)
        if (actionName.startsWith('arc:action:')) {
            return actionName;
        }
        
        if (actionName.startsWith('run')) {
            eventAction = actionName.slice(3);
            eventAction = eventAction.charAt(0).toLowerCase() + eventAction.slice(1);
        }
        
        return `arc:action:${eventAction}`;
    }

    /**
     * Register global event listeners
     */
    registerEventListeners() {
        this.actions.forEach((action, eventName) => {
            window.addEventListener(eventName, async (event) => {
                try {
                    this.log(`Executing action: ${action.name} from event: ${eventName}`, event.detail);
                    
                    const result = await action.function(event.detail || {});
                    
                    this.dispatchActionResult(eventName, action.name, result, event.detail);
                    
                } catch (error) {
                    console.error(`Action ${action.name} failed:`, error);
                    this.dispatchActionError(eventName, action.name, error, event.detail);
                }
            });
        });
    }

    /**
     * Register listener for action chains
     * Supports comma-separated action chains like: "showElement,fadeIn,trackEvent"
     */
    registerActionChainListener() {
        window.addEventListener('arc:action:chain', async (event) => {
            const { actions, data = {}, delay = 0 } = event.detail;
            
            if (!actions) {
                console.warn('No actions specified in action chain');
                return;
            }

            const actionList = typeof actions === 'string' 
                ? actions.split(',').map(a => a.trim())
                : actions;

            this.log(`Executing action chain: ${actionList.join(' -> ')}`, data);

            for (let i = 0; i < actionList.length; i++) {
                const actionName = actionList[i];
                
                try {
                    // Add delay between chained actions if specified
                    if (i > 0 && delay > 0) {
                        await this.delay(delay);
                    }
                    
                    await this.triggerAction(actionName, {
                        ...data,
                        chainIndex: i,
                        isChained: true,
                        totalChainLength: actionList.length
                    });
                    
                } catch (error) {
                    console.error(`Failed to execute chained action ${actionName}:`, error);
                    break; // Stop chain on error
                }
            }
        });
    }

    /**
     * Trigger an action by name
     */
    async triggerAction(actionName, data = {}) {
        const eventName = this.getEventName(actionName);
        
        this.log(`🎯 Triggering action: ${actionName} -> ${eventName}`);
        
        if (!this.actions.has(eventName)) {
            // Try to trigger as a chain if it contains commas
            if (actionName.includes(',')) {
                this.log(`⛓️ Treating as action chain: ${actionName}`);
                return this.triggerActionChain(actionName, data);
            }
            
            // console.error(`❌ Action ${actionName} not found. Available actions:`, this.getAvailableActionNames());
            // throw new Error(`Action ${actionName} not found. Available actions: ${this.getAvailableActionNames().join(', ')}`);
        }

        this.log(`✅ Dispatching event: ${eventName}`, data);
        window.dispatchEvent(new CustomEvent(eventName, {
            detail: data
        }));
    }

    /**
     * Trigger multiple actions as a chain
     */
    async triggerActionChain(actions, data = {}, delay = 0) {
        window.dispatchEvent(new CustomEvent('arc:action:chain', {
            detail: { actions, data, delay }
        }));
    }

    /**
     * Dispatch action success result
     */
    dispatchActionResult(eventName, actionName, result, originalData) {
        window.dispatchEvent(new CustomEvent(`${eventName}:success`, {
            detail: {
                action: actionName,
                result: result,
                originalData: originalData
            }
        }));
    }

    /**
     * Dispatch action error
     */
    dispatchActionError(eventName, actionName, error, originalData) {
        window.dispatchEvent(new CustomEvent(`${eventName}:error`, {
            detail: {
                action: actionName,
                error: error,
                originalData: originalData
            }
        }));
    }

    // Preset Action Implementations
    
    toggleElementVisibility(data, state) {
        const { selector, elementId, blockId, sectionId } = data;
        let target;

        if (selector) {
            target = document.querySelector(selector);
        } else if (elementId) {
            target = document.getElementById(elementId);
        } else if (blockId) {
            target = document.querySelector(`[data-block-id="${blockId}"]`);
        } else if (sectionId) {
            target = document.querySelector(`[data-section="${sectionId}"]`);
        }

        if (!target) {
            console.warn('Target element not found for visibility toggle:', data);
            return { success: false, error: 'Element not found' };
        }

        const currentDisplay = window.getComputedStyle(target).display;
        const isVisible = currentDisplay !== 'none';

        let newState;
        if (state === 'toggle') {
            newState = !isVisible;
        } else {
            newState = state;
        }

        target.style.display = newState ? 'block' : 'none';
        
        // Dispatch visibility change event
        target.dispatchEvent(new CustomEvent('visibility-changed', {
            detail: { visible: newState, action: state }
        }));

        return { success: true, visible: newState, element: target };
    }

    triggerCartAction(action) {
        // Trigger cart-related events that other systems can listen to
        window.dispatchEvent(new CustomEvent('cart:action', {
            detail: { action }
        }));
        return { success: true, action };
    }

    triggerModalAction(data, action) {
        const { modalId, content } = data;
        window.dispatchEvent(new CustomEvent('modal:action', {
            detail: { action, modalId, content }
        }));
        return { success: true, action, modalId };
    }

    triggerAnimation(data, animationType) {
        const { selector, elementId, blockId, duration = 300 } = data;
        let target;

        if (selector) {
            target = document.querySelector(selector);
        } else if (elementId) {
            target = document.getElementById(elementId);
        } else if (blockId) {
            target = document.querySelector(`[data-block-id="${blockId}"]`);
        }

        if (!target) {
            console.warn('Target element not found for animation:', data);
            return { success: false, error: 'Element not found' };
        }

        // Add animation class and remove after duration
        target.classList.add(`animate-${animationType}`);
        
        setTimeout(() => {
            target.classList.remove(`animate-${animationType}`);
        }, duration);

        return { success: true, animationType, element: target };
    }

    scrollToElement(data) {
        const { selector, elementId, behavior = 'smooth', block = 'start' } = data;
        let target;

        if (selector) {
            target = document.querySelector(selector);
        } else if (elementId) {
            target = document.getElementById(elementId);
        }

        if (!target) {
            console.warn('Target element not found for scroll:', data);
            return { success: false, error: 'Element not found' };
        }

        target.scrollIntoView({ behavior, block });
        return { success: true, element: target };
    }

    scrollToTop() {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return { success: true };
    }

    submitForm(data) {
        const { formId, selector } = data;
        let form;

        if (formId) {
            form = document.getElementById(formId);
        } else if (selector) {
            form = document.querySelector(selector);
        }

        if (!form) {
            console.warn('Form not found for submission:', data);
            return { success: false, error: 'Form not found' };
        }

        form.submit();
        return { success: true, form };
    }

    resetForm(data) {
        const { formId, selector } = data;
        let form;

        if (formId) {
            form = document.getElementById(formId);
        } else if (selector) {
            form = document.querySelector(selector);
        }

        if (!form) {
            console.warn('Form not found for reset:', data);
            return { success: false, error: 'Form not found' };
        }

        form.reset();
        return { success: true, form };
    }

    trackAnalyticsEvent(data) {
        const { event, category, label, value, ...customData } = data;
        
        const eventData = {
            event: event || 'custom_action',
            event_category: category || 'interaction',
            event_label: label || 'action_triggered',
            ...(value !== undefined && { event_value: value }),
            ...customData
        };

        if (typeof window !== 'undefined' && window.dataLayer) {
            window.dataLayer.push(eventData);
        }

        window.dispatchEvent(new CustomEvent('analytics-event', {
            detail: eventData
        }));

        return { success: true, eventData };
    }

    delay(duration) {
        return new Promise(resolve => setTimeout(resolve, duration));
    }

    redirect(url) {
        if (!url) {
            console.warn('No URL provided for redirect');
            return { success: false, error: 'No URL provided' };
        }
        
        window.location.href = url;
        return { success: true, url };
    }

    dispatchCustomEvent(data) {
        const { eventName, detail = {} } = data;
        
        if (!eventName) {
            console.warn('No event name provided for custom event');
            return { success: false, error: 'No event name provided' };
        }

        window.dispatchEvent(new CustomEvent(eventName, { detail }));
        return { success: true, eventName, detail };
    }

    // Utility methods
    
    extractActionName(path) {
        const fileName = path.split('/').pop();
        return fileName.replace('.jsx', '');
    }

    getAvailableActionNames() {
        return Array.from(this.actions.values()).map(action => action.name);
    }

    getAvailableEvents() {
        return Array.from(this.actions.keys());
    }

    getPresetActions() {
        return Array.from(this.presetActions.keys());
    }

    log(message, data = null) {
        if (this.debugMode) {
            if (data) {
                console.log(`[EventSystem] ${message}`, data);
            } else {
                console.log(`[EventSystem] ${message}`);
            }
        }
    }
}

// Create singleton instance
const enhancedEventSystem = new EnhancedEventSystem();

// Global helper functions
window.triggerAction = (actionName, data) => enhancedEventSystem.triggerAction(actionName, data);
window.triggerActionChain = (actions, data, delay) => enhancedEventSystem.triggerActionChain(actions, data, delay);
window.getAvailableActions = () => enhancedEventSystem.getAvailableActionNames();
window.getPresetActions = () => enhancedEventSystem.getPresetActions();

export { enhancedEventSystem };
export default enhancedEventSystem;
